import type { EquipmentItem } from '@sb/integrations/types';
import { ModbusFunctionCodeEnum } from '@sb/integrations/utils/modbus/ModbusFunctionCode';
import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class NetworkRequestRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.NetworkRequest', 'NetworkRequest');
  }

  protected validate({ args, ctx }: CanonicalValidateArgs): void {
    type NetworkRequestArgs = Extract<
      RcStep.Arguments,
      { argumentKind: 'NetworkRequest' }
    >;
    const a = args as NetworkRequestArgs;

    if (a.command.kind === 'ModbusTCPServer') {
      this.checkModbusRegisterId(a);
      this.checkModbusReadVariables(a);

      const equipmentConfig = this.findEquipment(ctx, a.command.equipmentId);

      this.checkEquipmentExists(equipmentConfig, a);
      this.checkRegisterExistsIfModbus(equipmentConfig, a);

      return;
    }

    this.throwUnsupportedCommand(a);
  }

  /**
   * Validate Modbus register ID presence.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkModbusRegisterId(
    args: Extract<RcStep.Arguments, { argumentKind: 'NetworkRequest' }>,
  ): void {
    if (args.command.registerId === '') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Modbus request requires a register ID',
        fieldId: 'command',
      });
    }
  }

  /**
   * Validate Modbus read variable list has entries.
   * Throws ActionRequiredError if empty.
   *
   * @throws {ActionRequiredError}
   */
  private checkModbusReadVariables(
    args: Extract<RcStep.Arguments, { argumentKind: 'NetworkRequest' }>,
  ): void {
    if (
      args.command.request.kind ===
        ModbusFunctionCodeEnum.ReadHoldingRegisters &&
      args.command.request.variableIDList.length === 0
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Modbus read request requires variables to be set',
        fieldId: 'command',
      });
    }
  }

  /**
   * Ensure equipment exists. Throws if not found.
   *
   * @throws {ActionRequiredError}
   */
  private checkEquipmentExists(
    equipmentConfig: EquipmentItem | undefined,
    args: Extract<RcStep.Arguments, { argumentKind: 'NetworkRequest' }>,
  ): void {
    if (!equipmentConfig) {
      throw new ActionRequiredError({
        kind: 'missingEquipment',
        message: `Unable to find device id ${args.command.equipmentId} in equipment manager.`,
      });
    }
  }

  /**
   * Ensure register exists when command kind is Modbus.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkRegisterExistsIfModbus(
    equipmentConfig: EquipmentItem | undefined,
    args: Extract<RcStep.Arguments, { argumentKind: 'NetworkRequest' }>,
  ): void {
    if (
      args.command.kind === 'ModbusTCPServer' &&
      equipmentConfig?.config.kind === 'ModbusTCPServer'
    ) {
      const equipmentRegister = equipmentConfig.config.registers?.find(
        (r) => r.id === args.command.registerId,
      );

      if (!equipmentRegister) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `Unable to find register id ${args.command.registerId} in equipment device configuration.`,
        });
      }
    }
  }

  /**
   * Throw an unsupported command error.
   *
   * @returns never
   * @throws {ActionRequiredError}
   */
  private throwUnsupportedCommand(
    args: Extract<RcStep.Arguments, { argumentKind: 'NetworkRequest' }>,
  ): never {
    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `${args.command.kind} is an unsupported command kind`,
    });
  }
}

export const NetworkRequestValidation: ValidationRule =
  new NetworkRequestRule().toValidationRule();
